import { SearchUserWithMarketFilters, searchUserWithMarket } from '@/pages/Users/api/services/searchUserWithMarkets';
import { UpdateUserActivationInput, UserActivationActionEnum, UserWithMarket } from '@/models/gen/graphql';
import { Validation, createNotification, dedupe, onEnter } from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AirportGroupDropdown } from '@/components/AirportDropdown';
import { Badge } from 'react-bootstrap';
import Dropdown from '@/components/Dropdown';
import EditUserPasswordModal from '@/components/EditUsersModal/EditUserPasswordModal';
import Filters from '@/components/Filters';
import FormButton from '@/components/FormButton';
import FormField from '@/components/FormField';
import HasPermission from '@/components/HasPermission';
import { LoadingBlur } from '@/components/LoadingSpinner';
import PageInfo from '@/components/PageInfo';
import RoleDropdown from '@/components/RoleDropdown';
import { Toast } from '@/models';
import UserModal from '@/pages/Users/components/UserModal';
import UsersTable from './Table';
import deleteUserBulk from '@/api/services/users/deleteUserBulk';
import updateUserActivationBulk from '@/api/services/users/updateUserActivationBulk';
import useConfirmation from '@/hooks/useConfirmation';
import useUsersTableState from './hook';

type UserTableState = {
  selected: string[];
  selectedUserId?: string[];
  resetPassword: boolean;
  search: string;
};

type SearchTableState = {
  rows: UserWithMarket[];
  totalCount: number;
  endCursor: string;
  hasNextPage: boolean;
  loading: boolean;
};

const initUserTableState: UserTableState = {
  selected: [],
  selectedUserId: undefined,
  resetPassword: false,
  search: '',
};

const initSearchTableState: SearchTableState = {
  rows: [],
  totalCount: 0,
  endCursor: undefined,
  hasNextPage: false,
  loading: false,
};

const UsersTablePage = (): JSX.Element => {
  const [state, setState] = useState(initUserTableState);
  const { resetPassword, selected, selectedUserId } = state;

  const [{ sorting }] = useUsersTableState(({ state }) => [{ sorting: state.sorting }]);

  const [tableState, setTableState] = useState<SearchTableState>(initSearchTableState);
  const { rows, totalCount, hasNextPage, loading } = tableState;

  const lastFilters = useRef<SearchUserWithMarketFilters>(undefined);

  const selectedRows = useMemo(
    (): UserWithMarket[] => rows?.filter((row: UserWithMarket): boolean => selected.includes(row.id)) || [],
    [rows, selected]
  );

  const onSelect = (idOrIds: string | string[]): void => {
    if (Array.isArray(idOrIds)) {
      setState((current): UserTableState => ({ ...current, selected: idOrIds }));
    } else {
      setState(
        (current): UserTableState => ({
          ...current,
          selected: current.selected.includes(idOrIds) ? current.selected.filter((id) => id !== idOrIds) : [...current.selected, idOrIds],
        })
      );
    }
  };

  const confirmDelete = useConfirmation();

  const runSearch = useCallback(
    async (filters: SearchUserWithMarketFilters): Promise<void> => {
      if (tableState.loading) return;
      setTableState((current) => ({ ...current, loading: true }));

      const page = await searchUserWithMarket(filters);

      setTableState(() => ({
        rows: page?.rows || [],
        totalCount: page.totalCount,
        endCursor: page.endCursor,
        hasNextPage: page.hasNextPage,
        loading: false,
      }));
      document.querySelector('.RouteContent').scrollTop = 0;
    },
    [tableState.loading]
  );

  const onFetchMore = useCallback(async (): Promise<void> => {
    if (!hasNextPage || !rows?.length) return;
    if (tableState.loading) return;
    setTableState((current) => ({ ...current, loading: true }));

    const page = await searchUserWithMarket(lastFilters.current, {
      after: tableState.endCursor,
    });

    setTableState((current) => ({
      rows: dedupe([...current.rows, ...page.rows], 'id'),
      totalCount: page.totalCount,
      endCursor: page.endCursor,
      hasNextPage: page.hasNextPage,
      loading: false,
    }));
  }, [hasNextPage, rows?.length, tableState.loading, tableState.endCursor]);

  const onSubmit = useCallback(
    async (filters): Promise<void> => {
      const searchFilters: SearchUserWithMarketFilters = {
        active: Validation.isNumber(filters?.active) ? filters.active : undefined,
        roleId: Validation.isValidUUID(filters?.roleId) ? filters.roleId : undefined,
        search: filters?.search,
        airportCode: filters?.airportCode,
        sorting,
      };
      lastFilters.current = searchFilters;
      await runSearch(searchFilters);
    },
    [sorting, runSearch]
  );

  // Re-query when sorting changes
  useEffect((): void => {
    if (!lastFilters.current) return;
    onSubmit(lastFilters.current || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const onToggleActive = async (user: UserWithMarket, isChecked: boolean): Promise<void> => {
    try {
      const input: UpdateUserActivationInput = {
        userId: user?.id,
        action: isChecked ? UserActivationActionEnum.Activate : UserActivationActionEnum.Deactivate,
      };
      setTableState(
        (current): SearchTableState => ({
          ...current,
          rows: current?.rows?.map(
            (row: UserWithMarket): UserWithMarket => (row?.id === user?.id ? { ...row, active: isChecked ? 1 : 0 } : row)
          ),
        })
      );
      const res = await updateUserActivationBulk([input]);
      if (!res?.usersUpdated) throw new Error('Failed to update User activation.');
    } catch (err) {
      createNotification('Error', Toast.Type.DANGER, err?.message || err);
      await runSearch(lastFilters.current || {});
    }
  };

  const onResetPassword = (user: UserWithMarket): void => {
    setState(
      (current): UserTableState => ({
        ...current,
        selected: [user?.id],
        resetPassword: true,
      })
    );
  };

  const onDelete = async (): Promise<void> => {
    try {
      await confirmDelete();
      await deleteUserBulk(selected);
      setState((): UserTableState => initUserTableState);
      await runSearch(lastFilters.current || {});
    } catch (err) {
      console.error('Cannot delete user', err);
    }
  };

  const onEdit = (userId?: string[]): void => {
    if (userId) setState((current): UserTableState => ({ ...current, selectedUserId: userId }));
    else setState((current): UserTableState => ({ ...current, selectedUserId: undefined }));
  };

  const onCreate = (): void => setState((current): UserTableState => ({ ...current, selectedUserId: [null] }));

  const onSubmitModal = (): void => {
    setState((current): UserTableState => ({ ...current, selectedUserId: undefined }));
    runSearch(lastFilters.current || {});
  };

  const onHide = (): void => {
    setState((): UserTableState => initUserTableState);
    runSearch(lastFilters.current || {});
  };

  return (
    <>
      <Filters
        name="userFilters"
        submitOnMount
        onSubmit={onSubmit}
        onReset={(): void => setState((): UserTableState => initUserTableState)}
        primary={({ values, onChange }): JSX.Element => {
          const { roleId, active, airportCode, search } = values;
          const handleChange = (name: string, value: any): void => onChange({ target: { name, value } });
          return (
            <>
              <RoleDropdown name="roleId" onChange={(value) => handleChange('roleId', value)} value={roleId} />
              <Dropdown
                name="active"
                value={!Validation.isNil(active) && !Number.isNaN(active) ? `${active}` : undefined}
                onChange={(value) => {
                  if (Validation.isNil(value) && Number.isNaN(value)) return handleChange('active', undefined);
                  handleChange('active', parseInt(value));
                }}
                options={{ locale: { 'Select...': 'Status' } }}
                items={[
                  { label: 'Inactive', value: '0' },
                  { label: 'Active', value: '1' },
                ]}
              />
              <AirportGroupDropdown
                name="airportCode"
                onChange={(value) => handleChange('airportCode', value)}
                value={airportCode}
                options={{ locale: { 'Select...': 'Airport' } }}
              />
              <FormField
                name="search"
                onChange={onChange}
                onKeyDown={onEnter(() => onSubmit(values))}
                value={search || ''}
                placeholder="Search"
                condensed
                style={{ width: 300 }}
              />
            </>
          );
        }}
        alternate={(): JSX.Element => (
          <>
            {/* Create Button */}
            <HasPermission name="allowCreateUserProfile">
              <FormButton icon={<i className="sv sv-plus-square fs-4" />} name="CREATE_USER" variant="outline-gray" onClick={onCreate}>
                Add User
              </FormButton>
            </HasPermission>
            {/* Edit Bulk Button */}
            <FormButton
              icon={<i className="sv sv-layers fs-4" />}
              name="EDIT_USER"
              variant="outline-gray"
              onClick={(): void => onEdit(selectedRows.map((row) => row?.id))}
              disabled={!selected.length}
            >
              Edit User{selected.length > 1 ? 's' : ''}
              {selected.length > 1 && (
                <Badge className="ms-2" pill bg="success">
                  {selected.length}
                </Badge>
              )}
            </FormButton>
            {/* Delete Button */}
            <FormButton
              icon={<i className="sv sv-trash2 fs-4" />}
              tooltip={
                <>
                  Delete User{selected.length > 1 ? 's' : ''}
                  {selected.length > 1 && (
                    <Badge className="ms-2" pill bg="success">
                      {selected.length}
                    </Badge>
                  )}
                </>
              }
              name="DELETE_USER"
              variant="outline-gray"
              onClick={onDelete}
              disabled={!selected.length}
            />
          </>
        )}
      />
      <PageInfo>
        {rows.length} / {totalCount ?? 0} Users
      </PageInfo>
      {loading && (
        <PageInfo>
          <i className="fa fa-spinner fa-pulse" />
        </PageInfo>
      )}
      <LoadingBlur loading={loading} />
      <UsersTable
        data={rows}
        selected={selected}
        onSelect={onSelect}
        onEdit={onEdit}
        onResetPassword={onResetPassword}
        onToggleActive={onToggleActive}
        fetchMore={onFetchMore}
      />
      <UserModal
        userIds={selectedUserId}
        onSubmit={onSubmitModal}
        onHide={() => {
          setState((current): UserTableState => ({ ...current, selectedUserId: undefined }));
        }}
      />
      <EditUserPasswordModal show={resetPassword} data={resetPassword ? selectedRows[0] : undefined} onHide={onHide} />
    </>
  );
};

export default UsersTablePage;
